
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

const AuthGuard = ({ children, requireAuth = true }: AuthGuardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session && requireAuth) {
          toast({
            variant: "destructive",
            title: "認証エラー",
            description: "このページにアクセスするにはログインが必要です",
          });
          navigate("/login");
        }
        
        setIsChecking(false);
      } catch (error) {
        console.error("Auth check error:", error);
        setIsChecking(false);
        if (requireAuth) {
          navigate("/login");
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" && requireAuth) {
        navigate("/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, requireAuth, toast]);

  // Don't render children until we've checked authentication
  if (isChecking && requireAuth) {
    return <div className="min-h-screen flex items-center justify-center">確認中...</div>;
  }

  return <>{children}</>;
};

export default AuthGuard;
